import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { MONO_FONT, PAGE_FONT } from "../../components/metrics/metricsUi";
import {
  recaptureHeadline,
  recaptureProgressPercent,
  recaptureScopeLabel,
  recaptureStatusMessage,
} from "../../utils/snapshotHealthRecaptureProgress";

const fmtCount = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("en-US");
};

const RecaptureProgressModal = ({
  open,
  run,
  reportDateLabel,
  ui,
  onHide,
  onDone,
  onRetry,
}) => {
  const { colors } = ui;
  const progress = (run && run.progress) || {};
  const status = run && run.status;
  const percent = recaptureProgressPercent(progress);
  const scope = recaptureScopeLabel(progress);
  const running = status === "running" || !status;
  const failed = status === "failed";
  const completed = status === "completed";
  const hasCounts = progress.total != null && Number(progress.total) >= 0;
  const preparing = running && !(Number(progress.processed) > 0);
  const showCounts = hasCounts && !preparing;
  const Icon = completed
    ? CheckCircleOutlineIcon
    : failed
      ? ErrorOutlineIcon
      : HourglassEmptyIcon;
  const iconColor = completed
    ? ui.positive
    : failed
      ? ui.danger
      : ui.accent;

  const scopeSummary = () => {
    const clients = Number(progress.client_count) || 0;
    const warehouses = Number(progress.warehouse_count) || 0;
    if (clients > 1 || warehouses > 1) {
      const clientLabel = `${clients} client${clients === 1 ? "" : "s"}`;
      const warehouseLabel = `${warehouses} warehouse${
        warehouses === 1 ? "" : "s"
      }`;
      return `Recapturing ${clientLabel} / ${warehouseLabel}`;
    }
    return scope || "Selected inventory";
  };

  return (
    <Dialog
      open={open}
      onClose={running ? onHide : onDone}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: PAGE_FONT,
        },
      }}
    >
      <DialogContent sx={{ pt: 3, pb: 1.5, px: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
          <Icon sx={{ fontSize: 28, color: iconColor, mt: "2px" }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 0.4 }}>
              {recaptureHeadline(run)}
            </Typography>
            {preparing && (progress.current_client_name || progress.current_warehouse_name) ? (
              <>
                {progress.current_client_name ? (
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    {progress.current_client_name}
                  </Typography>
                ) : null}
                {progress.current_warehouse_name ? (
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    {progress.current_warehouse_name}
                  </Typography>
                ) : null}
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                  {scopeSummary()}
                </Typography>
                {scope && (progress.client_count > 1 || progress.warehouse_count > 1) ? (
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary, mt: 0.35 }}>
                    Current: {scope}
                  </Typography>
                ) : null}
              </>
            )}
          </Box>
        </Box>

        {showCounts ? (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                mb: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontFamily: MONO_FONT,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                {fmtCount(progress.processed)} / {fmtCount(progress.total)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                SKUs processed
                {percent != null ? ` · ${percent}%` : ""}
              </Typography>
            </Box>
            <LinearProgress
              variant={percent == null ? "indeterminate" : "determinate"}
              value={percent || 0}
              sx={{
                height: 8,
                borderRadius: 99,
                backgroundColor: ui.flatBg,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  backgroundColor: failed
                    ? ui.danger
                    : completed
                      ? ui.positive
                      : ui.accent,
                },
              }}
            />
            {running && progress.batch_size ? (
              <Typography
                sx={{ mt: 0.75, fontSize: 12, color: colors.textTertiary }}
              >
                Current batch: {fmtCount(progress.batch_size)} SKUs
              </Typography>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ mb: 1.5 }}>
            <LinearProgress
              sx={{
                height: 8,
                borderRadius: 99,
                backgroundColor: ui.flatBg,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 99,
                  backgroundColor: ui.accent,
                },
              }}
            />
          </Box>
        )}

        <Typography sx={{ fontSize: 13, color: colors.textPrimary, mb: 0.75 }}>
          {recaptureStatusMessage(run)}
        </Typography>
        {completed && reportDateLabel ? (
          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
            Snapshot successfully recaptured for {reportDateLabel}.
          </Typography>
        ) : null}
        {running ? (
          <Typography sx={{ fontSize: 12, color: colors.textTertiary, mt: 1 }}>
            Please keep this window open. This may take a few minutes. You can
            hide this dialog; recapture continues on the server.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {running ? (
          <Button onClick={onHide} sx={{ textTransform: "none" }}>
            Hide
          </Button>
        ) : null}
        {failed ? (
          <>
            <Button onClick={onDone} sx={{ textTransform: "none" }}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={onRetry}
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
          </>
        ) : null}
        {completed ? (
          <Button
            variant="contained"
            onClick={onDone}
            sx={{ textTransform: "none" }}
          >
            Done
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default RecaptureProgressModal;
